<script setup>
import {ref, computed, onMounted, onBeforeUnmount} from 'vue'
import {useViewStore} from '@/stores/view'
import api from '@/services/api'

const props = defineProps({
    viewId: String,
    filterKey: Number
})

// Dynamic View Store
const viewStore = useViewStore(props.viewId)

// Establish local state
const options = ref(null)
const selectedValue = ref(null)

// Keep track of destruction state to maintain safety checks
let isDestroyed = false
onBeforeUnmount(() => {
    isDestroyed = true
})

// Computed properties
const filter = computed(() => {
    return viewStore.filters?.[props.filterKey] || null
})

const tableName = computed(() => {
    return viewStore.tableName || null
})

const value = computed({
    get() {
        return filter.value ? filter.value.value : undefined
    },
    set(newValue) {
        if (!filter.value) return
        viewStore.setFilterProp({
            key: props.filterKey,
            name: 'value',
            value: newValue
        })
    }
})

// Methods
const setOptions = async () => {
    // Guard against calling API with missing parameters
    if (!tableName.value || !filter.value?.columnName) return

    try {
        const obj = await api.post('api/ListingController/getSelectFilterOptions', {
            tableName: tableName.value,
            columnName: filter.value.columnName
        })

        // Safety check: verify component hasn't been unmounted before setting local state
        if (!isDestroyed) {
            const defaultOptions = [
                {value: undefined, text: 'Select…'},
                {value: '--unassigned--', text: '-- Unassigned --'}
            ]
            options.value = defaultOptions.concat(obj.data || [])
        }
    } catch (err) {
        if (!isDestroyed) {
            options.value = [{value: undefined, text: 'Failed to load options'}]
        }
    }
}

// Lifecycle Hooks (created/mounted)
onMounted(() => {
    setOptions()
})
</script>

<template>
    <span>
        <svg v-if="!options" class="loader uil-default" height="22px" preserveAspectRatio="xMidYMid"
             viewBox="0 0 100 100" width="22px" xmlns="http://www.w3.org/2000/svg">
            <rect class="bk" fill="none" height="100" width="100" x="0" y="0"></rect>
            <rect fill="#999999" height="20" rx="5" ry="5" transform="rotate(0 50 50) translate(0 -30)" width="7"
                  x="46.5" y="40">
                <animate attributeName="opacity" begin="0s" dur="1s" from="1" repeatCount="indefinite" to="0"></animate>
            </rect>
            <rect fill="#999999" height="20" rx="5" ry="5" transform="rotate(30 50 50) translate(0 -30)" width="7"
                  x="46.5" y="40">
                <animate attributeName="opacity" begin="0.08333333333333333s" dur="1s" from="1" repeatCount="indefinite"
                         to="0"></animate>
            </rect>
            <rect fill="#999999" height="20" rx="5" ry="5" transform="rotate(60 50 50) translate(0 -30)" width="7"
                  x="46.5" y="40">
                <animate attributeName="opacity" begin="0.16666666666666666s" dur="1s" from="1" repeatCount="indefinite"
                         to="0"></animate>
            </rect>
            <rect fill="#999999" height="20" rx="5" ry="5" transform="rotate(90 50 50) translate(0 -30)" width="7"
                  x="46.5" y="40">
                <animate attributeName="opacity" begin="0.25s" dur="1s" from="1" repeatCount="indefinite"
                         to="0"></animate>
            </rect>
            <rect fill="#999999" height="20" rx="5" ry="5" transform="rotate(120 50 50) translate(0 -30)" width="7"
                  x="46.5" y="40">
                <animate attributeName="opacity" begin="0.3333333333333333s" dur="1s" from="1" repeatCount="indefinite"
                         to="0"></animate>
            </rect>
            <rect fill="#999999" height="20" rx="5" ry="5" transform="rotate(150 50 50) translate(0 -30)" width="7"
                  x="46.5" y="40">
                <animate attributeName="opacity" begin="0.4166666666666667s" dur="1s" from="1" repeatCount="indefinite"
                         to="0"></animate>
            </rect>
            <rect fill="#999999" height="20" rx="5" ry="5" transform="rotate(180 50 50) translate(0 -30)" width="7"
                  x="46.5" y="40">
                <animate attributeName="opacity" begin="0.5s" dur="1s" from="1" repeatCount="indefinite"
                         to="0"></animate>
            </rect>
            <rect fill="#999999" height="20" rx="5" ry="5" transform="rotate(210 50 50) translate(0 -30)" width="7"
                  x="46.5" y="40">
                <animate attributeName="opacity" begin="0.5833333333333334s" dur="1s" from="1" repeatCount="indefinite"
                         to="0"></animate>
            </rect>
            <rect fill="#999999" height="20" rx="5" ry="5" transform="rotate(240 50 50) translate(0 -30)" width="7"
                  x="46.5" y="40">
                <animate attributeName="opacity" begin="0.6666666666666666s" dur="1s" from="1" repeatCount="indefinite"
                         to="0"></animate>
            </rect>
            <rect fill="#999999" height="20" rx="5" ry="5" transform="rotate(270 50 50) translate(0 -30)" width="7"
                  x="46.5" y="40">
                <animate attributeName="opacity" begin="0.75s" dur="1s" from="1" repeatCount="indefinite"
                         to="0"></animate>
            </rect>
            <rect fill="#999999" height="20" rx="5" ry="5" transform="rotate(300 50 50) translate(0 -30)" width="7"
                  x="46.5" y="40">
                <animate attributeName="opacity" begin="0.8333333333333334s" dur="1s" from="1" repeatCount="indefinite"
                         to="0"></animate>
            </rect>
            <rect fill="#999999" height="20" rx="5" ry="5" transform="rotate(330 50 50) translate(0 -30)" width="7"
                  x="46.5" y="40">
                <animate attributeName="opacity" begin="0.9166666666666666s" dur="1s" from="1" repeatCount="indefinite"
                         to="0"></animate>
            </rect>
        </svg>
        <select v-if="options" v-model="value" class="form-select">
            <option v-for="option in options" :value="option.value"
                    :key="option.value !== undefined ? option.value : 'default'">
                {{ option.text }}
            </option>
        </select>
    </span>
</template>